import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";
import { database, auth } from '../../firebaseConfig';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiChevronDown,
  FiChevronUp,
  FiBook,
  FiHome,
  FiTrash2,
  FiCheckCircle,
  FiHelpCircle,
  FiBarChart2,
  FiRefreshCw
} from 'react-icons/fi';
import PageLayout from '../PageLayout';
import SEO from '../SEO';
import LoadingSpinner from '../loader2';
import { useTheme } from '../../context/ThemeContext';

const AttemptedQuestionsPage = () => {
  const [organizedQuestions, setOrganizedQuestions] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    const fetchAttemptedQuestions = async () => {
      try {
        const userId = auth.currentUser?.uid;
        if (!userId) {
          navigate('/');
          return;
        }

        const userRef = doc(database, "users", userId);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const attemptedQuestionIds = userData.attempted_questions || [];

          if (attemptedQuestionIds.length > 0) {
            const chunkSize = 30;
            const questionsRef = collection(database, "questions");

            const chunks = [];
            for (let i = 0; i < attemptedQuestionIds.length; i += chunkSize) {
              chunks.push(attemptedQuestionIds.slice(i, i + chunkSize));
            }

            const fetchedQuestions = [];
            for (const chunk of chunks) {
              const q = query(questionsRef, where("id", "in", chunk));
              const querySnapshot = await getDocs(q);
              fetchedQuestions.push(...querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data()
              })));
            }

            const organized = fetchedQuestions.reduce((acc, question) => {
              const { category } = question;
              if (!acc[category]) acc[category] = [];
              acc[category].push(question);
              return acc;
            }, {});

            setOrganizedQuestions(organized);

            // Initially collapse all categories
            const initialExpandedState = Object.keys(organized).reduce((acc, category) => {
              acc[category] = false;
              return acc;
            }, {});
            setExpandedCategories(initialExpandedState);
          }
        }
      } catch (err) {
        console.error("Error fetching attempted questions:", err);
      } finally {
        setLoading(false);
      }
    };

    if (auth.currentUser) {
      fetchAttemptedQuestions();
    } else {
      navigate('/');
    }

    const preventRightClick = (e) => e.preventDefault();
    document.addEventListener("contextmenu", preventRightClick);

    return () => {
      document.removeEventListener("contextmenu", preventRightClick);
    };
  }, [navigate]);

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleResetAttemptedQuestions = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      const userRef = doc(database, "users", userId);
      await updateDoc(userRef, {
        attempted_questions: []
      });

      setOrganizedQuestions({});
      alert('Attempted questions have been cleared.');
    } catch (error) {
      console.error("Error resetting attempted questions:", error);
    }
  };

  // Theme-based classes
  const cardClass = isDark
    ? 'bg-dark-secondary/50 border border-white/10'
    : 'bg-white border border-gray-200 shadow-lg';

  const categoryBgClass = isDark
    ? 'bg-dark-tertiary/50 hover:bg-dark-tertiary/70'
    : 'bg-gray-50 hover:bg-gray-100';

  const textClass = isDark ? 'text-white' : 'text-gray-800';
  const subTextClass = isDark ? 'text-gray-400' : 'text-gray-600';

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner />
        </div>
      </PageLayout>
    );
  }

  const totalQuestions = Object.values(organizedQuestions).reduce((sum, questions) => sum + questions.length, 0);
  const totalCategories = Object.keys(organizedQuestions).length;

  return (
    <>
      <SEO
        title="Attempted Questions - Prepify | Review Your NET Practice History"
        description="Review all your attempted NUST Entry Test questions organized by category."
        keywords="Attempted Questions, NET Practice History, NUST Question Review"
        url="/attempted-questions"
      />
      <PageLayout>
        <div className="container mx-auto py-6 px-4 max-w-5xl">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="bg-gradient-to-br from-purple-600 via-violet-600 to-pink-500 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
              </div>
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl">
                    <FiBook className="text-2xl" />
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold">Attempted Questions</h1>
                    <p className="text-purple-100 text-sm">Review your learning journey</p>
                  </div>
                </div>

                {/* Stats Cards */}
                {totalQuestions > 0 && (
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="bg-white/20 backdrop-blur-md rounded-xl p-3 border border-white/30">
                      <div className="flex items-center gap-2">
                        <FiBarChart2 className="text-lg" />
                        <div>
                          <p className="text-xs text-purple-100">Total Questions</p>
                          <p className="text-xl font-bold">{totalQuestions}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-md rounded-xl p-3 border border-white/30">
                      <div className="flex items-center gap-2">
                        <FiBook className="text-lg" />
                        <div>
                          <p className="text-xs text-purple-100">Categories</p>
                          <p className="text-xl font-bold">{totalCategories}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Questions Section */}
          {Object.keys(organizedQuestions).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(organizedQuestions).map(([category, questions], categoryIndex) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: categoryIndex * 0.1 }}
                  className={`rounded-xl overflow-hidden ${cardClass}`}
                >
                  <motion.button
                    whileTap={{ scale: 0.99 }}
                    onClick={() => toggleCategory(category)}
                    className={`w-full p-4 transition-all duration-300 flex items-center justify-between ${categoryBgClass}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg shadow-lg">
                        <FiBook className="text-white text-lg" />
                      </div>
                      <div className="text-left">
                        <h2 className={`text-lg font-bold ${textClass}`}>{category}</h2>
                        <p className={`text-xs ${subTextClass}`}>{questions.length} question{questions.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <motion.div
                      animate={{ rotate: expandedCategories[category] ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className={`p-1.5 rounded-lg ${isDark ? 'bg-dark-tertiary' : 'bg-white shadow-md'}`}
                    >
                      {expandedCategories[category] ? (
                        <FiChevronUp className="text-purple-500 text-lg" />
                      ) : (
                        <FiChevronDown className="text-purple-500 text-lg" />
                      )}
                    </motion.div>
                  </motion.button>

                  <AnimatePresence>
                    {expandedCategories[category] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="p-4 space-y-3">
                          {questions.map((question, idx) => (
                            <motion.div
                              key={idx}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.05 }}
                              className={`rounded-xl p-4 ${isDark ? 'bg-dark-tertiary/30 border border-white/5' : 'bg-gray-50 border border-gray-100'}`}
                            >
                              <div className="flex items-start gap-3 mb-3">
                                <div className="p-1.5 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex-shrink-0">
                                  <FiHelpCircle className="text-white text-sm" />
                                </div>
                                <div className="flex-grow">
                                  <h3 className={`font-semibold text-sm ${isDark ? 'text-purple-400' : 'text-purple-700'} mb-1`}>
                                    Question {idx + 1}
                                  </h3>
                                  <p className={`text-sm leading-relaxed ${textClass}`}>
                                    {question.question}
                                  </p>
                                </div>
                              </div>

                              {question.solution && (
                                <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                                  <div className="flex items-start gap-3">
                                    <div className="p-1.5 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex-shrink-0">
                                      <FiCheckCircle className="text-white text-sm" />
                                    </div>
                                    <div className="flex-grow">
                                      <h4 className={`font-semibold text-sm ${isDark ? 'text-green-400' : 'text-green-700'} mb-1`}>Solution</h4>
                                      <p className={`text-sm leading-relaxed p-3 rounded-lg ${isDark ? 'bg-green-900/20 text-gray-300' : 'bg-green-50 text-gray-700'}`}>
                                        {question.solution}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`rounded-2xl p-10 text-center ${cardClass}`}
            >
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 3
                }}
                className="inline-block mb-4"
              >
                <div className={`p-4 rounded-full ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                  <FiBook className={`text-4xl ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
              </motion.div>
              <h2 className={`text-2xl font-bold mb-3 ${textClass}`}>No Questions Yet</h2>
              <p className={`text-base mb-6 max-w-md mx-auto ${subTextClass}`}>
                Start taking quizzes to see your attempted questions here. Your learning journey begins now! 🚀
              </p>
              <button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:opacity-90 transition-all"
              >
                <div className="flex items-center gap-2">
                  <FiHome className="text-lg" />
                  Go to Dashboard
                </div>
              </button>
            </motion.div>
          )}

          {/* Action Buttons */}
          {Object.keys(organizedQuestions).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-6 flex flex-col sm:flex-row gap-3"
            >
              <button
                onClick={() => navigate('/')}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-4 rounded-xl hover:opacity-90 transition-all font-semibold shadow-lg flex items-center justify-center gap-2"
              >
                <FiHome className="text-lg" />
                Back to Dashboard
              </button>
              <button
                onClick={handleResetAttemptedQuestions}
                className="flex-1 bg-gradient-to-r from-red-500 to-rose-600 text-white py-3 px-4 rounded-xl hover:opacity-90 transition-all font-semibold shadow-lg flex items-center justify-center gap-2"
              >
                <FiTrash2 className="text-lg" />
                Clear All Questions
              </button>
            </motion.div>
          )}
        </div>
      </PageLayout>
    </>
  );
};

export default AttemptedQuestionsPage;